// frontend/src/components/AutoConfirmToggle.tsx
// Компонент переключателя режима автоподтверждения (только для администратора)
// Версия: 2.1.0 - Props-based интерфейс
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { AutoMode as AutoModeIcon, PanTool as ManualIcon } from '@mui/icons-material';

interface AutoConfirmToggleProps {
  /** Текущее состояние: включено/выключено */
  enabled: boolean;
  /** Callback при переключении */
  onToggle: (enabled: boolean) => void;
  /** Идёт загрузка состояния */
  loading?: boolean;
  /** Идёт сохранение изменения */
  updating?: boolean;
  /** Компактный режим (только переключатель без описания) */
  compact?: boolean;
  /** Информация о последнем изменении */
  lastUpdated?: {
    by: string;
    at: string;
  };
}

/**
 * Переключатель режима автоподтверждения заявок
 * Доступен только администраторам
 */
export const AutoConfirmToggle: React.FC<AutoConfirmToggleProps> = ({
  enabled,
  onToggle,
  loading = false,
  updating = false,
  compact = false,
  lastUpdated,
}) => {
  // Обработчик переключения
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onToggle(event.target.checked);
  };

  // Форматирование даты
  const formatDate = (dateStr: string): string => {
    try {
      return new Date(dateStr).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  // Компактный режим - только переключатель
  if (compact) {
    if (loading) {
      return <CircularProgress size={20} />;
    }

    return (
      <Tooltip
        title={
          enabled
            ? 'Автоматический режим: заявки подтверждаются автоматически при совпадении терминала'
            : 'Ручной режим: требуется подтверждение оператором'
        }
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={handleChange}
                disabled={updating}
                color={enabled ? 'error' : 'primary'}
                size="small"
              />
            }
            label={
              <Typography
                variant="body2"
                fontWeight="bold"
                color={enabled ? 'error.main' : 'primary.main'}
              >
                {enabled ? 'АВТОМАТ' : 'РУЧНОЙ'}
              </Typography>
            }
            labelPlacement="start"
            sx={{ mr: 0 }}
          />
          {updating && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </Box>
      </Tooltip>
    );
  }

  // Полный режим - с описанием
  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        backgroundColor: enabled ? 'rgba(211, 47, 47, 0.08)' : 'rgba(25, 118, 210, 0.04)',
        borderLeft: 4,
        borderColor: enabled ? 'error.main' : 'primary.main',
        transition: 'all 0.3s ease',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Иконка */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: enabled ? 'error.light' : 'primary.light',
          }}
        >
          {enabled ? (
            <AutoModeIcon sx={{ color: 'error.dark' }} />
          ) : (
            <ManualIcon sx={{ color: 'primary.main' }} />
          )}
        </Box>

        {/* Описание */}
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Режим подтверждения заявок
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {enabled
              ? '🤖 Автоматический — заявки подтверждаются при совпадении номера терминала сигнала с номером SSAS в заявке'
              : '👤 Ручной — оператор проверяет и подтверждает каждую заявку'}
          </Typography>
          {lastUpdated && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
              Изменено: {lastUpdated.by}, {formatDate(lastUpdated.at)}
            </Typography>
          )}
        </Box>

        {/* Переключатель */}
        {loading ? (
          <CircularProgress size={32} />
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={handleChange}
                disabled={updating}
                color="error"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'error.main',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'error.main',
                  },
                }}
              />
            }
            label={
              <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={enabled ? 'error.main' : 'primary.main'}
                >
                  {enabled ? 'ВКЛ' : 'ВЫКЛ'}
                </Typography>
                {updating && <CircularProgress size={12} sx={{ mt: 0.5 }} />}
              </Box>
            }
            labelPlacement="bottom"
            sx={{ m: 0 }}
          />
        )}
      </Box>
    </Paper>
  );
};

export default AutoConfirmToggle;
